// DOB Calculator Utility Functions
import westernZodiac from '../data/westernZodiac.json';
import chineseZodiac from '../data/chineseZodiac.json';

/**
 * Calculate Life Path Number (numerology)
 * Reduces full DOB to a single digit (or master number 11, 22, 33)
 */
export const calculateLifePathNumber = (month, day, year) => {
  const reduceToSingleDigit = (num) => {
    let n = num;
    while (n > 9 && n !== 11 && n !== 22 && n !== 33) {
      n = String(n).split('').reduce((sum, d) => sum + parseInt(d), 0);
    }
    return n;
  };

  const m = reduceToSingleDigit(parseInt(month));
  const d = reduceToSingleDigit(parseInt(day));
  const y = reduceToSingleDigit(parseInt(year));

  return reduceToSingleDigit(m + d + y);
};

/**
 * Get the meaning/description of a Life Path Number
 */
export const getLifePathMeaning = (number) => {
  const meanings = {
    1: {
      title: 'The Leader',
      description: 'Independent, ambitious, and driven. You are a natural-born leader who thrives on innovation and individuality.',
      traits: ['Independent', 'Ambitious', 'Pioneering', 'Self-reliant', 'Innovative'],
    },
    2: {
      title: 'The Diplomat',
      description: 'Cooperative, sensitive, and harmonious. You are a natural peacemaker who excels in partnerships and collaboration.',
      traits: ['Cooperative', 'Diplomatic', 'Patient', 'Sensitive', 'Supportive'],
    },
    3: {
      title: 'The Communicator',
      description: 'Creative, expressive, and social. You are gifted with words and artistic talent, inspiring others through self-expression.',
      traits: ['Creative', 'Expressive', 'Optimistic', 'Social', 'Inspirational'],
    },
    4: {
      title: 'The Builder',
      description: 'Practical, disciplined, and reliable. You bring order to chaos and build things that last through hard work and dedication.',
      traits: ['Practical', 'Disciplined', 'Loyal', 'Organized', 'Hardworking'],
    },
    5: {
      title: 'The Adventurer',
      description: 'Dynamic, versatile, and freedom-loving. You embrace change and seek variety, learning through experience and travel.',
      traits: ['Adventurous', 'Versatile', 'Dynamic', 'Curious', 'Progressive'],
    },
    6: {
      title: 'The Nurturer',
      description: 'Responsible, caring, and devoted. You are driven by love and service, creating harmony in home and community.',
      traits: ['Responsible', 'Caring', 'Compassionate', 'Devoted', 'Protective'],
    },
    7: {
      title: 'The Seeker',
      description: 'Analytical, introspective, and spiritual. You seek deeper truths and understanding, drawn to wisdom and knowledge.',
      traits: ['Analytical', 'Introspective', 'Wise', 'Spiritual', 'Perceptive'],
    },
    8: {
      title: 'The Achiever',
      description: 'Powerful, ambitious, and goal-oriented. You are destined for material success and possess strong executive ability.',
      traits: ['Ambitious', 'Authoritative', 'Efficient', 'Successful', 'Strategic'],
    },
    9: {
      title: 'The Humanitarian',
      description: 'Compassionate, generous, and selfless. You are here to serve humanity with broad vision and universal love.',
      traits: ['Compassionate', 'Generous', 'Idealistic', 'Creative', 'Tolerant'],
    },
    11: {
      title: 'The Intuitive (Master Number)',
      description: 'Highly intuitive, inspirational, and visionary. A master number representing spiritual insight and enlightenment.',
      traits: ['Intuitive', 'Visionary', 'Inspirational', 'Sensitive', 'Idealistic'],
    },
    22: {
      title: 'The Master Builder (Master Number)',
      description: 'The most powerful number. You can turn grand dreams into reality, combining vision with practical mastery.',
      traits: ['Visionary', 'Practical', 'Ambitious', 'Disciplined', 'Masterful'],
    },
    33: {
      title: 'The Master Teacher (Master Number)',
      description: 'The most spiritually evolved number. You uplift humanity through selfless service and profound compassion.',
      traits: ['Compassionate', 'Selfless', 'Healing', 'Devoted', 'Uplifting'],
    },
  };
  return meanings[number] || meanings[9];
};

/**
 * Calculate Birthday Number (just the day reduced)
 */
export const calculateBirthdayNumber = (day) => {
  let d = parseInt(day);
  if (d > 9 && d !== 11 && d !== 22) {
    d = String(d).split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  }
  return d;
};

/**
 * Get Western Zodiac sign from month/day
 */
export const getWesternZodiac = (month, day) => {
  const m = parseInt(month);
  const d = parseInt(day);

  for (const sign of westernZodiac) {
    // Handle Capricorn which spans year boundary (Dec 22 - Jan 19)
    if (sign.startMonth > sign.endMonth) {
      if ((m === sign.startMonth && d >= sign.startDay) ||
          (m === sign.endMonth && d <= sign.endDay)) {
        return sign;
      }
    } else {
      if ((m === sign.startMonth && d >= sign.startDay) ||
          (m === sign.endMonth && d <= sign.endDay) ||
          (m > sign.startMonth && m < sign.endMonth)) {
        return sign;
      }
    }
  }
  return westernZodiac[0]; // fallback to Aries
};

/**
 * Get Chinese Zodiac sign from year
 */
export const getChineseZodiac = (year) => {
  const y = parseInt(year);
  // The cycle: Rat=0, Ox=1, Tiger=2, etc. 2008 is Rat year.
  const animals = ['Rat', 'Ox', 'Tiger', 'Rabbit', 'Dragon', 'Snake',
                   'Horse', 'Goat', 'Monkey', 'Rooster', 'Dog', 'Pig'];
  const index = ((y - 2008) % 12 + 12) % 12;
  const animalName = animals[index];
  return chineseZodiac.find(z => z.animal === animalName) || chineseZodiac[0];
};

/**
 * Get the Chinese zodiac element for a specific year (changes every 2 years)
 */
export const getChineseYearElement = (year) => {
  const elements = ['Metal', 'Metal', 'Water', 'Water', 'Wood', 'Wood',
                    'Fire', 'Fire', 'Earth', 'Earth'];
  const index = (parseInt(year) % 10);
  // Map: 0,1=Metal, 2,3=Water, 4,5=Wood, 6,7=Fire, 8,9=Earth
  return elements[index];
};

/**
 * Get Yin or Yang for a specific year
 */
export const getYinYang = (year) => {
  return parseInt(year) % 2 === 0 ? 'Yang' : 'Yin';
};

/**
 * Calculate day of the week for a date
 */
export const getDayOfWeek = (month, day, year) => {
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
};

/**
 * Get the meaning of being born on a specific day of the week
 */
export const getDayOfWeekMeaning = (dayName) => {
  const meanings = {
    'Sunday': { planet: 'Sun', trait: 'Creative and confident, you radiate warmth and attract attention naturally.' },
    'Monday': { planet: 'Moon', trait: 'Intuitive and emotional, you are deeply connected to your inner world and family.' },
    'Tuesday': { planet: 'Mars', trait: 'Energetic and courageous, you are a natural fighter who tackles challenges head-on.' },
    'Wednesday': { planet: 'Mercury', trait: 'Communicative and clever, you excel in learning, teaching, and connecting ideas.' },
    'Thursday': { planet: 'Jupiter', trait: 'Optimistic and generous, you are blessed with luck and a philosophical mind.' },
    'Friday': { planet: 'Venus', trait: 'Artistic and loving, you are drawn to beauty, harmony, and meaningful relationships.' },
    'Saturday': { planet: 'Saturn', trait: 'Disciplined and wise, you possess maturity beyond your years and strong resilience.' },
  };
  return meanings[dayName] || meanings['Sunday'];
};

/**
 * Calculate age in various units
 */
export const calculateAge = (month, day, year) => {
  const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let ageYears = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    ageYears--;
  }

  const diffMs = today - birthDate;
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.floor(totalDays / 7);
  const totalMonths = ageYears * 12 + (today.getMonth() - birthDate.getMonth() + (today.getDate() < birthDate.getDate() ? -1 : 0));
  const totalHours = totalDays * 24;

  return {
    years: ageYears,
    totalMonths,
    totalWeeks,
    totalDays,
    totalHours,
  };
};

/**
 * Calculate next birthday info
 */
export const getNextBirthday = (month, day) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const m = parseInt(month) - 1;
  const d = parseInt(day);

  let nextBirthday = new Date(today.getFullYear(), m, d);
  if (nextBirthday <= today) {
    nextBirthday = new Date(today.getFullYear() + 1, m, d);
  }

  const diffMs = nextBirthday - today;
  const daysUntil = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = days[nextBirthday.getDay()];

  return {
    daysUntil,
    dayOfWeek: dayName,
    date: nextBirthday,
  };
};

/**
 * Get birthstone for a given month
 */
export const getBirthstone = (month) => {
  const stones = {
    1: { stone: 'Garnet', meaning: 'Protection and strength' },
    2: { stone: 'Amethyst', meaning: 'Wisdom and tranquility' },
    3: { stone: 'Aquamarine', meaning: 'Courage and clarity' },
    4: { stone: 'Diamond', meaning: 'Eternal love and invincibility' },
    5: { stone: 'Emerald', meaning: 'Rebirth and fertility' },
    6: { stone: 'Pearl', meaning: 'Purity and innocence' },
    7: { stone: 'Ruby', meaning: 'Passion and vitality' },
    8: { stone: 'Peridot', meaning: 'Prosperity and good fortune' },
    9: { stone: 'Sapphire', meaning: 'Truth and sincerity' },
    10: { stone: 'Opal', meaning: 'Hope and creativity' },
    11: { stone: 'Topaz', meaning: 'Joy and abundance' },
    12: { stone: 'Tanzanite', meaning: 'Transformation and spiritual growth' },
  };
  return stones[parseInt(month)] || stones[1];
};

/**
 * Get birth flower for a given month
 */
export const getBirthFlower = (month) => {
  const flowers = {
    1: { flower: 'Carnation', meaning: 'Love and fascination' },
    2: { flower: 'Violet', meaning: 'Loyalty and devotion' },
    3: { flower: 'Daffodil', meaning: 'New beginnings' },
    4: { flower: 'Daisy', meaning: 'Innocence and purity' },
    5: { flower: 'Lily of the Valley', meaning: 'Sweetness and humility' },
    6: { flower: 'Rose', meaning: 'Love and gratitude' },
    7: { flower: 'Larkspur', meaning: 'Positivity and dignity' },
    8: { flower: 'Gladiolus', meaning: 'Strength and integrity' },
    9: { flower: 'Aster', meaning: 'Wisdom and valor' },
    10: { flower: 'Marigold', meaning: 'Warmth and creativity' },
    11: { flower: 'Chrysanthemum', meaning: 'Joy and longevity' },
    12: { flower: 'Narcissus', meaning: 'Hope and prosperity' },
  };
  return flowers[parseInt(month)] || flowers[1];
};

/**
 * Get the season of birth
 */
export const getBirthSeason = (month) => {
  const m = parseInt(month);
  if (m >= 3 && m <= 5) return { season: 'Spring', emoji: '🌸', trait: 'Creative and optimistic, spring babies are natural starters.' };
  if (m >= 6 && m <= 8) return { season: 'Summer', emoji: '☀️', trait: 'Energetic and outgoing, summer babies radiate warmth and confidence.' };
  if (m >= 9 && m <= 11) return { season: 'Autumn', emoji: '🍂', trait: 'Thoughtful and balanced, autumn babies value wisdom and reflection.' };
  return { season: 'Winter', emoji: '❄️', trait: 'Resilient and deep, winter babies possess inner strength and determination.' };
};

/**
 * Calculate the digit sum of the full birthday
 */
export const calculateBirthdayDigitSum = (month, day, year) => {
  const fullStr = String(parseInt(month)) + String(parseInt(day)) + String(parseInt(year));
  return fullStr.split('').reduce((sum, digit) => sum + parseInt(digit), 0);
};

/**
 * Calculate digital root (reduce to single digit)
 */
export const calculateDigitalRoot = (number) => {
  let n = number;
  while (n > 9) {
    n = String(n).split('').reduce((sum, d) => sum + parseInt(d), 0);
  }
  return n;
};

/**
 * Get Bible words/phrases that match a given gematria value
 * Searches across all ciphers like BibleMatchScreen does
 * Only returns ciphers that have phrase matches
 */
export const getBibleMatches = (value) => {
  try {
    const phraseDB = require('../data/biblePhraseDB.json');
    const numStr = String(value);

    if (!phraseDB) return {};

    const results = {};

    // Search phraseDB across all ciphers — phrases only
    Object.keys(phraseDB).forEach(cipherName => {
      const phrases = (phraseDB[cipherName] && phraseDB[cipherName][numStr]) || [];

      if (phrases.length > 0) {
        results[cipherName] = {
          value: parseInt(numStr),
          phrases: phrases.slice(0, 10),
        };
      }
    });

    return results;
  } catch (error) {
    return {};
  }
};

/**
 * Fetch famous birthdays from Wikipedia "On This Day" API
 * Uses byabbe.se free API — no key required
 */
export const fetchFamousBirthdays = async (month, day) => {
  try {
    const m = parseInt(month);
    const d = parseInt(day);
    const response = await fetch(`https://byabbe.se/on-this-day/${m}/${d}/births.json`);
    if (!response.ok) throw new Error('API unavailable');
    const data = await response.json();
    // Return top 10 births, sorted by most notable (Wikipedia usually lists most famous first)
    return (data.births || []).slice(0, 10).map(b => ({
      year: b.year,
      description: b.description,
      wikipedia: b.wikipedia && b.wikipedia[0] ? b.wikipedia[0].wikipedia : null,
    }));
  } catch (error) {
    console.log('Famous birthdays API unavailable:', error.message);
    return [];
  }
};

/**
 * Fetch historical events from Wikipedia "On This Day" API
 */
export const fetchHistoricalEvents = async (month, day) => {
  try {
    const m = parseInt(month);
    const d = parseInt(day);
    const response = await fetch(`https://byabbe.se/on-this-day/${m}/${d}/events.json`);
    if (!response.ok) throw new Error('API unavailable');
    const data = await response.json();
    // Return top 10 events
    return (data.events || []).slice(0, 10).map(e => ({
      year: e.year,
      description: e.description,
      wikipedia: e.wikipedia && e.wikipedia[0] ? e.wikipedia[0].wikipedia : null,
    }));
  } catch (error) {
    console.log('Historical events API unavailable:', error.message);
    return [];
  }
};
